import { Component, OnInit } from '@angular/core';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { AggregationGroup } from 'src/app/services/jsonrpc/stats-server.service';
import { Role } from 'src/app/services/jsonrpc/user-server.service';
import { AnalyticsMetric, StatsService } from 'src/app/services/stats.service';
import { UserService } from 'src/app/services/user.service';

type TimePerModuleEntry = {
  time: number;
  path: string;
  year: string; //parsable in number
  month: string; //parsable in number
  day: string; //parsable in number
  hour: string; //parsable in number
};

type TimePerModuleReport = TimePerModuleEntry[];

type ParsedPathEntry = {
  time: number;
  displayPath: string;
  nodeLink: string;
  dateTime: string;
};

type ConsolidatedPathEntry = {
  displayPath: string;
  nodeLink: string;
  totalTime: number;
  recordCount: number;
  records: ParsedPathEntry[];
};

type DayData = {
  date: string;
  records: ParsedPathEntry[];
  totalTime: number;
};

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit {
  public fromDate: NgbDate;
  public toDate: NgbDate;
  public selectedRole: string;
  public roles: Role[] = [];
  public reportData: ConsolidatedPathEntry[] = [];
  public filteredReportData: ConsolidatedPathEntry[] = [];
  public searchQuery: string = '';
  public isLoading: boolean = false;
  public hasNoResultsAfterFiltering: boolean = false;
  public showChartModal: boolean = false;
  public selectedEntry: ConsolidatedPathEntry | null = null;
  public daysWithData: DayData[] = [];
  public currentDayIndex: number = 0;
  public chartLabels: string[] = [];
  public chartDatasets: any[] = [];
  public chartOptions: any = {};
  public chartColors: any[] = [];

  constructor(private statsService: StatsService, private userService: UserService) {
    this.setDefaultDates();
  }

  public ngOnInit(): void {
    this.loadRoles();
  }

  private loadRoles(): void {
    this.userService.roles.subscribe(roles => {
      this.roles = roles;
      if (this.roles.length > 0) {
        this.selectedRole = this.roles[0].name;
      }
    });
  }

  public applyFilters(): void {
    // Convert NgbDate to Date objects for API call
    const fromDate = this.ngbDateToDate(this.fromDate);
    const toDate = this.ngbDateToDate(this.toDate);

    this.isLoading = true;
    this.statsService
      .fetchMetrics(
        AnalyticsMetric.time,
        null,
        ['path', 'year', 'month', 'day', 'hour'],
        { [AggregationGroup.roles]: this.selectedRole } as any,
        null,
        fromDate,
        toDate
      )
      .then((data: TimePerModuleReport) => {
        const parsedData = this.parseReportData(data);
        this.reportData = this.consolidateByPath(parsedData);
        this.hasNoResultsAfterFiltering = this.reportData.length === 0;
        this.searchQuery = '';
        this.filterReportData();
        this.isLoading = false;
      })
      .catch(error => {
        console.error('Error fetching metrics:', error);
        this.isLoading = false;
      });
  }

  private ngbDateToDate(ngbDate: NgbDate): Date {
    if (!ngbDate) {
      return null;
    }
    return new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day);
  }

  private setDefaultDates(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.toDate = new NgbDate(today.getFullYear(), today.getMonth() + 1, today.getDate());

    this.fromDate = new NgbDate(thirtyDaysAgo.getFullYear(), thirtyDaysAgo.getMonth() + 1, thirtyDaysAgo.getDate());
  }

  private parseReportData(data: TimePerModuleReport): ParsedPathEntry[] {
    return data
      .map(entry => ({
        time: entry.time,
        displayPath: this.parsePath(entry.path),
        nodeLink: this.getNodeLink(entry.path),
        dateTime: this.formatDateTime(entry.year, entry.month, entry.day, entry.hour)
      }))
      .filter(entry => entry.displayPath !== 'FILTER_OUT' && entry.time >= 1000);
  }

  private consolidateByPath(parsedData: ParsedPathEntry[]): ConsolidatedPathEntry[] {
    const consolidated = new Map<string, ConsolidatedPathEntry>();

    for (const entry of parsedData) {
      if (consolidated.has(entry.displayPath)) {
        const existing = consolidated.get(entry.displayPath)!;
        existing.totalTime += entry.time;
        existing.recordCount += 1;
        existing.records.push(entry);
      } else {
        consolidated.set(entry.displayPath, {
          displayPath: entry.displayPath,
          nodeLink: entry.nodeLink,
          totalTime: entry.time,
          recordCount: 1,
          records: [entry]
        });
      }
    }

    return Array.from(consolidated.values());
  }

  private getNodeLink(path: string): string {
    if (!path || path.trim() === '') {
      return '';
    }

    const withoutPrefix = path.replace(/^s8l:\/\/[^/]+\//, '');

    const [pathPart, queryPart] = withoutPrefix.split('?');

    const encodedPath = encodeURIComponent(pathPart).replace('(', '%2528').replace(')', '%2529');

    let nodeLink = `/node/${encodedPath}`;

    if (queryPart) {
      nodeLink += `?${queryPart}`;
    }

    return nodeLink;
  }

  private parsePath(path: string): string {
    if (!path || path.trim() === '') {
      return 'FILTER_OUT';
    }

    const withoutPrefix = path.replace(/^s8l:\/\/[^/]+\//, '');

    const withoutQuery = withoutPrefix.split('?')[0];

    const segments = withoutQuery.split('/').filter(segment => segment.trim() !== '');

    if (segments.length <= 1) {
      return 'FILTER_OUT';
    }

    segments.shift();

    if (segments.length === 0) {
      return 'FILTER_OUT';
    }

    return segments.map(segment => decodeURIComponent(segment)).join(' > ');
  }

  private formatDateTime(year: string, month: string, day: string, hour: string): string {
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    const h = parseInt(hour, 10);

    const date = new Date(y, m - 1, d, h, 0, 0);

    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };

    const dateStr = date.toLocaleDateString('en-US', options);
    // Format: "Nov 6, 2025, 14:30"
    return dateStr;
  }

  public filterReportData(): void {
    if (!this.searchQuery.trim()) {
      this.filteredReportData = this.reportData;
    } else {
      const query = this.searchQuery.toLowerCase();
      this.filteredReportData = this.reportData.filter(entry => entry.displayPath.toLowerCase().includes(query));
    }
  }

  public formatTime(milliseconds: number): string {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  public formatDuration(): string {
    if (!this.fromDate || !this.toDate) {
      return 'Last 30 days';
    }
    const fromDate = this.ngbDateToDate(this.fromDate);
    const toDate = this.ngbDateToDate(this.toDate);
    const diffTime = Math.abs(toDate.getTime() - fromDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `Last ${diffDays} days`;
  }

  public openChartModal(entry: ConsolidatedPathEntry): void {
    this.selectedEntry = entry;
    this.showChartModal = true;
    this.daysWithData = this.groupRecordsByDay(entry.records);
    this.currentDayIndex = 0;
    this.updateChartData();
  }

  private groupRecordsByDay(records: ParsedPathEntry[]): DayData[] {
    const dayMap = new Map<string, ParsedPathEntry[]>();

    for (const record of records) {
      // Extract date from dateTime (format: "Nov 6, 2025, 14:30")
      const dateMatch = record.dateTime.match(/^([A-Za-z]+ \d+, \d+)/);
      if (dateMatch) {
        const date = dateMatch[1];
        if (!dayMap.has(date)) {
          dayMap.set(date, []);
        }
        dayMap.get(date)!.push(record);
      }
    }

    return Array.from(dayMap.entries())
      .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
      .map(([date, dayRecords]) => ({
        date,
        records: dayRecords,
        totalTime: dayRecords.reduce((sum, r) => sum + r.time, 0)
      }));
  }

  private updateChartData(): void {
    if (this.daysWithData.length === 0 || this.currentDayIndex >= this.daysWithData.length) {
      return;
    }

    const dayData = this.daysWithData[this.currentDayIndex];
    const hourMap = new Map<number, number>();

    // Group records by hour and sum the time
    for (const record of dayData.records) {
      // Extract hour from dateTime (format: "Nov 6, 2025, 14:30")
      const hourMatch = record.dateTime.match(/(\d{2}):\d{2}$/);
      if (hourMatch) {
        const hour = parseInt(hourMatch[1], 10);
        const currentTime = hourMap.get(hour) || 0;
        hourMap.set(hour, currentTime + record.time);
      }
    }

    // Create labels for all 24 hours and data array
    const labels: string[] = [];
    const data: number[] = [];

    for (let h = 0; h < 24; h++) {
      labels.push(`${h.toString().padStart(2, '0')}:00`);
      data.push((hourMap.get(h) || 0) / 1000); // Convert to seconds
    }

    this.chartLabels = labels;
    this.chartDatasets = [
      {
        label: 'Time spent (seconds)',
        data,
        borderColor: '#1d273e',
        backgroundColor: 'rgba(29, 39, 62, 0.15)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointBackgroundColor: '#1d273e',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        borderWidth: 2
      }
    ];

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 750
      },
      scales: {
        yAxes: [
          {
            display: true,
            gridLines: {
              display: true,
              drawBorder: true
            },
            ticks: {
              beginAtZero: true,
              min: 0
            }
          }
        ],
        xAxes: [
          {
            display: true,
            gridLines: {
              display: true,
              drawBorder: true
            }
          }
        ]
      }
    };

    this.chartColors = [
      {
        backgroundColor: 'rgba(29, 39, 62, 0.15)',
        borderColor: '#1d273e',
        pointBackgroundColor: '#1d273e',
        pointBorderColor: '#fff',
        borderWidth: 2
      }
    ];
  }

  public nextDay(): void {
    if (this.currentDayIndex < this.daysWithData.length - 1) {
      this.currentDayIndex++;
      this.updateChartData();
    }
  }

  public previousDay(): void {
    if (this.currentDayIndex > 0) {
      this.currentDayIndex--;
      this.updateChartData();
    }
  }

  public get currentDay(): DayData | null {
    return this.daysWithData[this.currentDayIndex] || null;
  }

  public closeChartModal(): void {
    this.showChartModal = false;
    this.selectedEntry = null;
    this.daysWithData = [];
    this.currentDayIndex = 0;
  }
}
